import { Component, Inject, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { BankingService } from '../../features/banking/services/banking.service';
import { DialogRef } from '@angular/cdk/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ConfirmDialog } from '../../shared/components/confirm-dialog/confirm-dialog';
import { UpdateBankingDetailsComponent } from './update-transaction.component';
import { CreateTransactionDialog } from './create-transaction-dialog.component';
import { UploadBankingDialog } from './upload-csv/upload-banking-dialog';
import { AddTransactionDialog } from './add-transaction/add-transaction-dialog';

@Component({
  selector: 'app-banking-details',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    UpdateBankingDetailsComponent,
  ],
  templateUrl: './banking-details.component.html',
  styleUrl: './banking-details.component.scss',
})
export class BankingDetailsComponent {
  selectedTabIndex = 0;
  selectedTransaction = 'default';
  selectedOption = 'today';
  isDropdownOpen = false;
  showUncategorize = false;
  showAllTransaction = false;
  showAddTransaction = false;
  readonly dialog = inject(MatDialog);
  bankId: number;
  transactionData: any;
  transactions: any;
  withdrawal: any;
  deposit: any;
  transactionAmount: any;
  parentAccountId: any;
  isSubAccount: any;
  chartOfAccounts: any;
  transactionForm: FormGroup;
  categorizeForm: FormGroup;
  accountType: any;
  search: any = '';
  selectedDate: any;
  file: any;
  statementAmount: any;
  statements: any;
  categoryId: number;
  moneyInCategoriesExist: boolean = false;
  creditCards: any;
  matchedData: any;
  reconcilationIds: any[] = [];
  statementId: any;
  transactionIds: any[] = [];
  isSelectedAll: boolean = false;
  edit: boolean = false;
  isCreate: boolean = false;
  totalAmount: number = 0;
  pendingAmount: number = 0;
  statement: any;
  transactionType: any;
  statementDate: any;
  flow: string;
  categorySelection: any;
  manualAmount: any;
  transactionDetail: any;
  startDate: any;
  endDate: any;
  totals: any;
  dateRangeForm: FormGroup;
  unmatchedStatementsData: any[] = [];
  bankTransactionsData: any[] = [];
  transactionTotalData: any;
  transactionCategories: any;

  dateFilter = (date: Date | null): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Remove time component
    return date ? date <= today : false;
  };

  constructor(
    private route: ActivatedRoute,
    private bankingService: BankingService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.bankId = Number(id);
      this.findUnmatchedStatements(this.bankId);
      this.bankTransactions(this.bankId);
      this.findAllTransactionTotals(this.bankId);
      this.initForm();
      this.getTransactionCategories();
    }
  }

  initForm() {
    this.transactionForm = this.fb.group({
      offsetAccountId: this.bankId,
      fromAccount: [this.bankId],
      chartOfAccount: [''],
      receivedVia: [''],
      tds: [''],
      payment: [''],
      paid: [''],
      deposit: [''],
      customer: [''],
      date: ['', Validators.required],
      amount: [
        '',
        [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)],
      ],
      invoice: [''],
      description: [''],
      file: [''],
      flow: 'Debit',
      status: 'Manual',
    });

    this.categorizeForm = this.fb.group({
      category: ['', Validators.required],
      date: ['', Validators.required],
      reference: [''],
      description: [''],
    });

    this.dateRangeForm = this.fb.group({
      startDate: [''],
      endDate: [''],
    });
  }

  updateValidatorsBasedOnTransaction() {
    console.log(this.selectedTransaction);
    const selectedTransaction = this.selectedTransaction;

    // Reset validators
    this.transactionForm.get('fromAccount')?.clearValidators();
    this.transactionForm.get('tds')?.clearValidators();
    this.transactionForm.get('payment')?.clearValidators();
    this.transactionForm.get('paid')?.clearValidators();
    this.transactionForm.get('deposit')?.clearValidators();
    this.transactionForm.get('chartOfAccount')?.clearValidators();

    // Add validators conditionally for 'fromAccount'
    if (
      [
        'card payment',
        'owner drawings',
        'transfer from another account',
        'other income',
        'owner contribution',
      ].includes(selectedTransaction)
    ) {
      this.transactionForm
        .get('fromAccount')
        ?.setValidators(Validators.required);
    }

    // Add validators conditionally for 'chartOfAccount' (To Account)
    if (
      ['transfer to another account', 'card payment'].includes(
        selectedTransaction
      )
    ) {
      this.transactionForm
        .get('chartOfAccount')
        ?.setValidators(Validators.required);
    }

    // Add validators conditionally for other fields
    if (selectedTransaction === 'vendorAdvance') {
      this.transactionForm.get('tds')?.setValidators(Validators.required);
      this.transactionForm.get('payment')?.setValidators(Validators.required);
      this.transactionForm.get('paid')?.setValidators(Validators.required);
      this.transactionForm.get('deposit')?.setValidators(Validators.required);
    }

    // Update the validators for all fields
    this.transactionForm.get('fromAccount')?.updateValueAndValidity();
    this.transactionForm.get('tds')?.updateValueAndValidity();
    this.transactionForm.get('payment')?.updateValueAndValidity();
    this.transactionForm.get('paid')?.updateValueAndValidity();
    this.transactionForm.get('deposit')?.updateValueAndValidity();
    this.transactionForm.get('chartOfAccount')?.updateValueAndValidity();
  }

  selectTab(index: number) {
    this.selectedTabIndex = index;
  }

  getTransactionCategories() {
    this.bankingService.transactionCategories('').subscribe({
      next: (res) => {
        this.transactionCategories = res;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  openAddTransactionDialog() {
    const dialogRef = this.dialog.open(AddTransactionDialog, {
      disableClose: true,
      data: { bankId: this.bankId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // this.fetchAccountsOfBankAndCash();
    });
  }

  openUploadStatementDialog() {
    const dialogRef = this.dialog.open(UploadBankingDialog, {
      disableClose: true,
      data: { bankId: this.bankId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // this.fetchAccountsOfBankAndCash();
      this.findUnmatchedStatements(this.bankId);
      this.bankTransactions(this.bankId);
      this.findAllTransactionTotals(this.bankId);
    });
  }
  openDialog() {
    const dialogRef = this.dialog.open(UploadBankingDetailsDialog, {
      disableClose: true,
      data: this.bankId,
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.findUnmatchedStatements(this.bankId);
      this.bankTransactions(this.bankId);
      this.findAllTransactionTotals(this.bankId);
    });
  }

  onCreate() {
    let applyTo = '';

    if (this.pendingAmount > 0) {
      applyTo = 'Deposits';
    } else if (this.pendingAmount < 0) {
      applyTo = 'Withdrawals';
    }
    console.log(this.statement);

    const dialogRef = this.dialog.open(CreateTransactionDialog, {
      disableClose: true,
      data: {
        applyTo: applyTo,
        bankId: this.bankId,
        pendingAmount: Number(this.pendingAmount),
        statementDate: this.statement?.statementDate,
      },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.listMatchedPaymentsForTransaction(this.statement?.id);
    });
  }

  findUnmatchedStatements(bankAccountId: any) {
    this.bankingService
      .findUnmatchedStatements(
        parseFloat(bankAccountId),
        this.search,
        this.startDate,
        this.endDate
      )
      .subscribe({
        next: (res) => {
          this.unmatchedStatementsData = res;
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  bankTransactions(bankAccountId: any) {
    this.bankingService
      .bankTransactions(
        Number(bankAccountId),
        this.search,
        this.startDate,
        this.endDate
      )
      .subscribe({
        next: (res) => {
          this.bankTransactionsData = res;
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  findAllTransactionTotals(bankAccountId: any) {
    this.bankingService
      .findAllTransactionTotals(
        Number(bankAccountId),
        this.search,
        this.startDate,
        this.endDate
      )
      .subscribe({
        next: (res) => {
          this.totals = res;
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  toggleDropdown(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  toggleCategorize(event?: MouseEvent, statement?: any) {
    console.log(statement);

    if (event) {
      event.stopPropagation();
    }
    if (statement?.id) {
      this.statementId = statement?.id;
      this.statement = statement;
      this.manualAmount = statement?.amount;
      this.transactionType = statement?.type;
      this.statementDate = statement?.statementDate;
      this.totalAmount = 0;
      this.transactionIds = [];
      this.pendingAmount = 0;
      this.isCreate = false;
      const statementType = statement?.type?.toLowerCase();
      if (statementType === 'debit') {
        console.log('this.statement?.amount ', this.statement?.amount);

        this.pendingAmount -= this.statement?.amount || 0;

        console.log('this.pendingAmount', this.pendingAmount);
      } else if (statementType === 'credit') {
        this.pendingAmount += this.statement?.amount || 0;
      }
      this.listMatchedPaymentsForTransaction(statement?.id);
    }
    this.showUncategorize = !this.showUncategorize;    
  }

  listMatchedPaymentsForTransaction(id: any) {
    this.categorizeForm.reset();
    this.bankingService.listMatchedPaymentsForStatement(Number(id)).subscribe({
      next: (data) => {
        this.matchedData = data;
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  toggleAllTransaction(event?: MouseEvent, transaction?: any) {
    console.log('inside');
    console.log(transaction);

    if (event) {
      event.stopPropagation();
    }
    if (transaction?.id) {
      this.getTransactionDetails(transaction?.id);
    }
    if (this.transactionDetail?.id) {
      this.getTransactionDetails(this.transactionDetail?.id);
    }
    this.edit = false;
    this.showAllTransaction = !this.showAllTransaction;    
  }

  onEdit() {
    // this.getTransactionDetails(this.transactionDetail?.id)
    this.edit = true;
  }

  getTransactionDetails(id: any) {
    this.bankingService.bankTransaction(Number(id)).subscribe({
      next: (data) => {
        this.transactionDetail = data;
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  // categorize manually
  onCategorizeManually() {
    if (this.categorizeForm.invalid) {
      this.categorizeForm.markAllAsTouched();
      return;
    }

    const statusType = 'categorize';
    const transactionId = null;
    const statementId = this.statementId;
    const createTransactionInput = {
      bankAccountId: Number(this.bankId),
      amount: parseFloat(this.manualAmount),
      referenceNumber: this.categorizeForm.value.reference,
      description: this.categorizeForm.value.description,
      transactionCategory: this.categorizeForm.value.category,
      transactionType: this.transactionType
        ? this.transactionType.charAt(0).toUpperCase() +
          this.transactionType.slice(1).toLowerCase()
        : '',
      transactionDate: this.datePipe.transform(
        this.categorizeForm.value.date,
        'yyyy-MM-dd'
      ),
      // transactionDate: this.statementDate,
    };

    this.bankingService
      .categorizeTransactions(
        statusType,
        Number(statementId),
        transactionId,
        createTransactionInput
      )
      .subscribe({
        next: (data) => {
          this.toastr.success(
            'Transaction categorized successfully',
            'Success'
          );
          this.showUncategorize = !this.showUncategorize;
          this.findUnmatchedStatements(this.bankId);
          this.bankTransactions(this.bankId);
          this.findAllTransactionTotals(this.bankId);
        },
        error: (error) => {
          this.toastr.error(error, 'Error');
        },
      });
  }

  onFileSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.file = file;
    }
  }

  closeDropdown() {
    this.showAddTransaction = !this.showAddTransaction;
    this.selectedTransaction = 'default';
    // this.findUnmatchedStatements(this.bankId);
    // this.bankTransactions(this.bankId);
    // this.findAllTransactionTotals(this.bankId);
  }

  // on best match
  onBestMatch(id: any) {
    const statusType = 'match';
    this.transactionIds.push(id);
    const amount = Number();
    const createTransactionInput = {
      transactionStatus: 'Matched',
      flow: '',
      offsetAccountId: this.bankId,
      amount: amount,
    };
    console.log('On best match ', createTransactionInput);
  }

  toggleTransaction(data: any, event: Event) {
    const isChecked = (event.target as HTMLInputElement).checked;
    console.log('data', data);
    if (isChecked) {
      this.totalAmount = 0;
      console.log('data.transactionType', data.transactionType);

      if (data.transactionType === 'Credit') {
        this.totalAmount += data.amount || 0;
      } else if (data.transactionType === 'Debit') {
        this.totalAmount -= data.amount || 0;
      }
      this.transactionIds.push(data.id);
      console.log('pendingAmount', this.pendingAmount);
      console.log('totalAmount', this.totalAmount);
      console.log('statement?.amount', this.statement?.amount);

      this.pendingAmount = this.pendingAmount - this.totalAmount;
      // this.isCreate = this.totalAmount !== this.statement?.amount && this.totalAmount !== 0;
      this.isCreate = true;
    } else {
      this.transactionIds = this.transactionIds.filter((id) => id !== data.id);
      this.totalAmount = 0;
      if (data.transactionType === 'Credit') {
        this.totalAmount += data.amount || 0;
      } else if (data.transactionType === 'Debit') {
        this.totalAmount -= data.amount || 0;
      }
      this.pendingAmount = this.pendingAmount + this.totalAmount;
      // this.isCreate = this.totalAmount !== this.statement?.amount && this.totalAmount !== 0;
    }
  }

  selectAll() {
    if (this.isSelectedAll) {
      // Deselect all
      this.transactionIds = [];
      this.totalAmount = 0; // Reset the total amount
      this.isCreate = false;
      this.pendingAmount = this.totalAmount - this.statement?.amount;
    } else {
      // Select all
      this.transactionIds =
        this.matchedData?.possibleMatches?.map((data: any) => data.id) || [];
      this.totalAmount =
        this.matchedData?.possibleMatches?.reduce(
          (sum: number, data: any) => sum + (data.amount || 0),
          0
        ) || 0;
      this.isCreate = true;
      this.pendingAmount = this.totalAmount - this.statement?.amount;
    }
    this.isSelectedAll = !this.isSelectedAll;
  }
  // on possible match
  onMatch() {
    console.log('totalAmount', this.totalAmount);
    console.log('statementamount', this.statement?.amount);

    if (this.pendingAmount === 0 && this.transactionIds?.length > 0) {
      const transactionIds = [...this.transactionIds];
      const statusType = 'match';
      this.bankingService
        .matchOrUnmatchStatementToTransactions(
          statusType,
          this.statementId,
          transactionIds
        )
        .subscribe({
          next: (data) => {
            if (data) {
              this.toastr.success(
                'Transaction matched successfully',
                'Success'
              );
              this.showUncategorize = !this.showUncategorize;
              this.findUnmatchedStatements(this.bankId);
              this.bankTransactions(this.bankId);
              this.findAllTransactionTotals(this.bankId);
            }
          },
          error: (error) => {
            this.toastr.error(error?.message, 'Error');
          },
        });
    } else if (this.totalAmount === 0) {
      this.toastr.warning('Please select any transaction to match', 'Warning');
    } else {
      this.toastr.warning(
        'The total sum of the matched transactions is not equal to the amount in the statement. Create an adjustment to match the pending amount',
        'Balance mismatch!'
      );
    }
  }

  onUnmatch() {
    console.log('this.statementId', this.transactionDetail?.id);
    const transactionIds = [this.transactionDetail?.id];
    const statusType = 'unmatch';
    const statementId = this.transactionDetail?.matchedStatementId;
    this.bankingService
      .matchOrUnmatchStatementToTransactions(
        statusType,
        statementId,
        transactionIds
      )
      .subscribe({
        next: (data) => {
            this.toastr.success(
              'Transaction unmatched successfully',
              'Success'
            );
            this.showAllTransaction = !this.showAllTransaction;
            this.findUnmatchedStatements(this.bankId);
            this.bankTransactions(this.bankId);
            this.findAllTransactionTotals(this.bankId);
            
        },
        error: (error) => {
          this.toastr.error(error?.message, 'Error');
        },
      });
  }

  onUncategorized() {
    const statusType = 'uncategorize';
    const transactionId: number = this.transactionDetail?.id;
    const statementId: number = this.transactionDetail?.matchedStatementId;
    const createTransactionInput = {
      bankAccountId: Number(this.bankId),
      amount: parseFloat(this.transactionDetail?.amount),
    };

    this.bankingService
      .categorizeTransactions(
        statusType,
        Number(statementId),
        transactionId,
        createTransactionInput
      )
      .subscribe({
        next: (data) => {
          console.log(data);
          
          this.toastr.success(
            'Transaction uncategorized successfully',
            'Success'
          );
          this.showAllTransaction = !this.showAllTransaction;
          this.findUnmatchedStatements(this.bankId);
          this.bankTransactions(this.bankId);
          this.findAllTransactionTotals(this.bankId);
        },
        error: (error) => {
          this.toastr.error(error, 'Error');
        },
      });
  }

  onDateChange(event: any) {
    this.selectedDate = this.datePipe.transform(event.value, 'yyyy-MM-dd');
  }

  onDateChangeRange(event: any, type: string) {
    const formattedDate = this.datePipe.transform(event.value, 'yyyy-MM-dd');
    if (type === 'start') {
      this.startDate = formattedDate;
    } else if (type === 'end') {
      this.endDate = formattedDate;
    }
    if (this.startDate != null && this.endDate != null) {
      this.findUnmatchedStatements(this.bankId);
      this.bankTransactions(this.bankId);
      this.findAllTransactionTotals(this.bankId);
    }
  }

  onCancel() {
    this.transactionForm.reset();
  }

  onConfirmDelete(): void {
    const dialogRef = this.dialog.open(ConfirmDialog);
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.deleteTransaction(this.transactionDetail?.id);
      }
    });
  }

  deleteTransaction(id: any) {
    this.bankingService.removeBankTransaction(Number(id)).subscribe({
      next: (data) => {
        this.toastr.success('Transaction deleted successfully', 'Error');
        this.showAllTransaction = !this.showAllTransaction;
        this.findUnmatchedStatements(this.bankId);
        this.bankTransactions(this.bankId);
      },
      error: (error) => {
        this.toastr.error(error, 'Error');
      },
    });
  }

  removeFile(): void {
    this.file = null;
    const input = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  // Add transaction
  onSave() {
    console.log('this.transactionForm', this.transactionForm.controls);
    this.updateValidatorsBasedOnTransaction();
    if (this.transactionForm.invalid) {
      this.transactionForm.markAllAsTouched();
      return;
    }

    const fromAccount = this.transactionForm.value.fromAccount;
    const chartOfAccount = this.transactionForm.value.chartOfAccount;
    console.log(this.selectedTransaction);

    if (
      this.selectedTransaction === 'card payment' ||
      this.selectedTransaction === 'transfer to another account'
    ) {
      // First call   with `fromAccount` and flow as 'Debit'
      this.createTransaction(this.bankId, 'Debit', () => {
        // Second call with `chartOfAccount` and flow as 'Credit'
        this.createTransaction(chartOfAccount, 'Credit', () => {
          this.toastr.success('Transaction added successfully', 'Success');
        });
      });
    } else if (this.selectedTransaction === 'owner drawings') {
      console.log('create transaction');

      this.createTransaction(fromAccount, 'Debit', () => {
        this.toastr.success('Transaction added successfully', 'Success');
      });
    } else if (this.selectedTransaction === 'Interest income') {
      this.createTransaction(this.bankId, 'Credit', () => {
        this.toastr.success('Transaction added successfully', 'Success');
      });
    } else if (
      this.selectedTransaction === 'transfer from another account' ||
      this.selectedTransaction === 'other income' ||
      this.selectedTransaction === 'owner contribution'
    ) {
      // First call with `fromAccount` and flow as 'Debit'
      this.createTransaction(fromAccount, 'Debit', () => {
        // Second call with `chartOfAccount` and flow as 'Credit'
        this.createTransaction(this.bankId, 'Credit', () => {
          this.toastr.success('Transaction added successfully', 'Success');
        });
      });
    }

    this.showAddTransaction = !this.showAddTransaction;
  }

  createTransaction(
    offsetAccountId: number,
    flow: 'Debit' | 'Credit',
    callback: () => void
  ) {
    console.log('log createTransaction');

    const transactionData = {
      date: this.datePipe.transform(
        this.transactionForm.value.date,
        'yyyy-MM-dd'
      ),
      transactionStatus: 'Manual',
      referenceNumber: this.transactionForm.value.invoice,
      amount: Number(this.transactionForm.value.amount),
      description: this.transactionForm.value.description,
      flow: flow, // Dynamic flow based on account type
      offsetAccountId: offsetAccountId, // Dynamic offsetAccountId
      transactionCategoryId: Number(this.categoryId),
    };

    // this.bankingService.createTransaction(transactionData).subscribe({
    //   next: (data) => {
    //     if (data) {
    //       callback();
    //     }
    //   },
    //   error: (error) => {
    //     this.toastr.error(error, 'Error');
    //   },
    // });
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.search = input.value;
    this.findUnmatchedStatements(this.bankId);
    this.bankTransactions(this.bankId);
    this.findAllTransactionTotals(this.bankId);
  }
}

@Component({
  selector: 'upload-banking-details-dialog',
  templateUrl: 'upload-banking-details-dialog.html',
  styleUrls: ['./banking-details.component.scss'],
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
})
export class UploadBankingDetailsDialog {
  file: any = null;
  accounts: any;
  search: any;

  constructor(
    private bankingService: BankingService,
    private toastr: ToastrService,
    private dialogRef: DialogRef,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    document.addEventListener('dragover', this.preventDefaultBehavior);
    document.addEventListener('drop', this.preventDefaultBehavior);
  }

  preventDefaultBehavior(event: DragEvent) {
    event.preventDefault();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();

    if (event.dataTransfer?.files.length) {
      this.onFileSelected({ target: { files: event.dataTransfer.files } });
    }
  }

  onFileSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      console.log('Selected file:', file);
      this.file = file;
    }
  }

  removeFile(): void {
    this.file = null;
    const input = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  onUpload() {
    const accountId = this.data;
    this.bankingService.bulkCreateTransactions(this.file, accountId).subscribe({
      next: (response) => {
        if (response && !response.errors) {
          this.toastr.success('Transactions uploaded successfully!', 'Success');
          console.log(accountId);
          this.dialogRef.close(accountId);
        } else if (response?.errors) {
          this.toastr.error(response?.errors[0]?.message, 'Error');
        }
      },
      error: (error) => {
        this.toastr.error(error, 'Bulk transaction creation failed');
        this.dialogRef.close();
      },
    });
  }
}
